import { useState, useEffect } from 'react'
import { supabase } from '../../../shared/lib/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import { useOrg } from '../../../contexts/OrgContext'
import { usePolicy } from '../../../contexts/PolicyContext'

const SECTIONS = [
  { key: 'document_control', label: 'Document Control', locked: true },
  { key: 'purpose', label: 'Purpose' },
  { key: 'scope', label: 'Scope' },
  { key: 'definitions', label: 'Definitions' },
  { key: 'policy_statements', label: 'Policy Statements' },
  { key: 'roles_and_responsibilities', label: 'Roles & Responsibilities' },
  { key: 'compliance_and_enforcement', label: 'Compliance & Enforcement' },
  { key: 'related_documents', label: 'Related Documents' }
]

function sectionToText(key, value) {
  if (value == null) return ''

  if (key === 'scope') {
    const applies = value.applies_to || value
    const exclusions = value.exclusions || ''
    if (typeof applies === 'string' && !value.exclusions) return applies
    return `Applies to: ${typeof applies === 'string' ? applies : applies}\nExclusions: ${exclusions}`
  }

  if (key === 'definitions') {
    if (!Array.isArray(value)) return String(value)
    return value.map(d => `${d.term}: ${d.definition}`).join('\n')
  }

  if (key === 'roles_and_responsibilities') {
    if (!Array.isArray(value)) return String(value)
    return value.map(r => `${r.role}: ${r.responsibilities}`).join('\n')
  }

  if (key === 'related_documents') {
    if (!Array.isArray(value)) return String(value)
    return value.join('\n')
  }

  if (key === 'policy_statements') {
    if (!Array.isArray(value)) return String(value)
    return value.map(s => {
      let text = ''
      if (s.section_id) text += `[${s.section_id}] `
      text += s.title || ''
      if (s.content) text += '\n' + s.content
      if (Array.isArray(s.bullets) && s.bullets.length > 0) {
        text += '\n' + s.bullets.map(b => `- ${b}`).join('\n')
      }
      return text
    }).join('\n\n')
  }

  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}

function textToSection(key, text) {
  if (key === 'scope') {
    const lines = text.split('\n')
    const appliesLine = lines.find(l => l.startsWith('Applies to:'))
    const exclusionsLine = lines.find(l => l.startsWith('Exclusions:'))
    if (appliesLine || exclusionsLine) {
      return {
        applies_to: appliesLine ? appliesLine.replace('Applies to:', '').trim() : '',
        exclusions: exclusionsLine ? exclusionsLine.replace('Exclusions:', '').trim() : ''
      }
    }
    return text
  }

  if (key === 'definitions') {
    return text.split('\n').filter(l => l.trim()).map(line => {
      const idx = line.indexOf(':')
      if (idx === -1) return { term: line.trim(), definition: '' }
      return { term: line.slice(0, idx).trim(), definition: line.slice(idx + 1).trim() }
    })
  }

  if (key === 'roles_and_responsibilities') {
    return text.split('\n').filter(l => l.trim()).map(line => {
      const idx = line.indexOf(':')
      if (idx === -1) return { role: line.trim(), responsibilities: '' }
      return { role: line.slice(0, idx).trim(), responsibilities: line.slice(idx + 1).trim() }
    })
  }

  if (key === 'related_documents') {
    return text.split('\n').filter(l => l.trim())
  }

  if (key === 'policy_statements') {
    const blocks = text.split(/\n\n+/)
    return blocks.filter(b => b.trim()).map(block => {
      const lines = block.split('\n')
      const firstLine = lines[0] || ''
      const match = firstLine.match(/^\[([^\]]+)\]\s*(.*)/)
      const section_id = match ? match[1] : ''
      const title = match ? match[2] : firstLine
      const bullets = []
      const contentLines = []
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].startsWith('- ')) {
          bullets.push(lines[i].slice(2))
        } else {
          contentLines.push(lines[i])
        }
      }
      const result = { title }
      if (section_id) result.section_id = section_id
      if (contentLines.length) result.content = contentLines.join('\n')
      if (bullets.length) result.bullets = bullets
      return result
    })
  }

  return text
}

function wordCount(text) {
  if (!text) return 0
  return text.trim().split(/\s+/).filter(Boolean).length
}

export default function SectionEditorPanel({ template, onClose, onSave }) {
  const { user } = useAuth()
  const { currentOrg } = useOrg() || {}
  const { updateTemplate } = usePolicy()

  const [activeSection, setActiveSection] = useState(SECTIONS[0].key)
  const [editedSections, setEditedSections] = useState({})
  const [dirtyKeys, setDirtyKeys] = useState(new Set())
  const [saving, setSaving] = useState(false)

  // Initialize edited sections from template
  useEffect(() => {
    if (template?.structured_content) {
      const initial = {}
      SECTIONS.forEach(({ key }) => {
        initial[key] = sectionToText(key, template.structured_content[key])
      })
      setEditedSections(initial)
      setDirtyKeys(new Set())
    }
  }, [template])

  const handleBackdropClick = () => {
    if (dirtyKeys.size > 0) {
      if (window.confirm('You have unsaved changes. Discard?')) onClose()
    } else {
      onClose()
    }
  }

  const handleCancel = () => {
    if (dirtyKeys.size > 0) {
      if (!window.confirm('You have unsaved changes. Discard?')) return
    }
    onClose()
  }

  const handleTextChange = (key, text) => {
    setEditedSections(prev => ({ ...prev, [key]: text }))
    setDirtyKeys(prev => {
      const next = new Set(prev)
      const original = sectionToText(key, template.structured_content?.[key])
      if (text !== original) {
        next.add(key)
      } else {
        next.delete(key)
      }
      return next
    })
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const updated = { ...template.structured_content }
      SECTIONS.forEach(({ key, locked }) => {
        if (!locked && editedSections[key] !== undefined) {
          updated[key] = textToSection(key, editedSections[key])
        }
      })

      await updateTemplate(template.id, {
        structured_content: updated,
        org_edited: true
      })

      const activeSectionDef = SECTIONS.find(s => s.key === activeSection)
      await supabase.from('org_template_revisions').insert({
        template_id: template.id,
        org_id: currentOrg?.id,
        content: JSON.stringify(updated),
        edit_method: 'inline',
        edited_by: user?.id,
        change_summary: `Section edit: ${activeSectionDef?.label || activeSection}`
      })

      setDirtyKeys(new Set())
      onSave()
    } catch (err) {
      console.error('Error saving section edits:', err)
    } finally {
      setSaving(false)
    }
  }

  // Legacy markdown fallback
  if (!template?.structured_content) {
    return (
      <div className="fixed inset-0 z-50">
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />
        <div className="absolute right-0 top-0 h-full w-[560px] bg-white dark:bg-gray-800 shadow-2xl flex flex-col">
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                This template uses legacy markdown format and cannot be edited section by section. Use the main Edit button to modify the full content.
              </p>
              <button className="btn-secondary" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const activeSectionDef = SECTIONS.find(s => s.key === activeSection)
  const currentText = editedSections[activeSection] || ''

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50" onClick={handleBackdropClick} />
      <div
        className="absolute right-0 top-0 h-full w-[560px] bg-white dark:bg-gray-800 shadow-2xl flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Edit Sections</h2>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 flex min-h-0">
          {/* Section nav - 35% */}
          <div className="w-[35%] border-r border-gray-200 dark:border-gray-700 overflow-y-auto py-2">
            {SECTIONS.map(({ key, label, locked }) => (
              <button
                key={key}
                onClick={() => setActiveSection(key)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                  activeSection === key
                    ? 'border-l-2 border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium'
                    : 'border-l-2 border-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <span className="flex-1 truncate">{label}</span>
                {locked && (
                  <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                )}
                {dirtyKeys.has(key) && (
                  <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                )}
              </button>
            ))}
          </div>

          {/* Section editor - 65% */}
          <div className="w-[65%] flex flex-col overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                {activeSectionDef?.label}
              </h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {wordCount(currentText)} words
              </span>
            </div>

            {activeSectionDef?.locked ? (
              <div className="flex-1">
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <span className="font-medium text-gray-600 dark:text-gray-300" title="Document control is managed automatically and cannot be edited here.">
                      Locked Section
                    </span>
                  </div>
                  <pre className="whitespace-pre-wrap font-sans">{currentText}</pre>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col">
                <textarea
                  value={currentText}
                  onChange={e => handleTextChange(activeSection, e.target.value)}
                  className="flex-1 min-h-[300px] w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white p-3 text-sm font-mono resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={`Enter ${activeSectionDef?.label?.toLowerCase()} content...`}
                />
                {template.source_template_id && (
                  <button
                    type="button"
                    onClick={() => {
                      const original = sectionToText(activeSection, template.structured_content?.[activeSection])
                      handleTextChange(activeSection, original)
                    }}
                    className="mt-2 text-xs text-primary-600 dark:text-primary-400 hover:underline self-start"
                  >
                    Reset to SSA original
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
          <button className="btn-secondary" onClick={handleCancel}>Cancel</button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || dirtyKeys.size === 0}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}
